<script lang="ts">
  import GameCard from "$lib/components/GameCard.svelte";
  import type {IGame} from "$lib/interfaces/IGame";
  import ProviderDropDown from "$lib/components/ModalContents/Search/ProviderDropDown.svelte";
  import TagDropdown from "$lib/elements/TagDropdown.svelte";
  import {onMount, onDestroy} from "svelte";
  import LoadMoreButton from "$lib/elements/LoadMoreButton.svelte";
  import {gameService} from "$lib/services/game.service";
  import {page} from "$app/stores";
  import type {Unsubscriber} from "svelte/store";

  // Props
  let { data } = $props();

  // State variables
  let games: IGame[] = $state([]);
  let currentPage = $state(0);
  let nextPage = $state(1);
  let totalElements = $state(0);
  let loading = $state(true); // Start with loading state true
  let gameProviderId = '';
  let tag = '';
  let searchValue = '';
  let categorySlug = $state(data.slug);
  let previousCategorySlug = $state('');
  let isInitialLoad = true;
  let dataLoaded = $state(false); // Track if data has been loaded at least once

  // Initialize promise immediately to show loading state
  let promise: Promise<IGame[]> = $state(new Promise((resolve) => {
    // This empty promise will be replaced in onMount
    // but ensures the loading state is shown immediately
  }));

  // Store subscription
  let pageSubscription: Unsubscriber;

  // Page configuration
  const pageConfig = {
    title: 'Casino',
    description: 'Casino page',
    keywords: 'casino, games, [slug], table games',
    og: {
      title: 'Casino',
      description: 'Casino page',
      image: 'https://forza.bet/assets/images/og/casino.jpg',
    },
  };

  /**
   * Initialize the component
   */
  onMount(() => {
    // Initial load of games
    promise = loadGames(false);

    // Subscribe to page store for category changes
    pageSubscription = page.subscribe(value => {
      if (isInitialLoad) {
        // Skip processing on initial subscription to avoid duplicate API calls
        isInitialLoad = false;
        return;
      }

      // Store the previous category slug
      previousCategorySlug = categorySlug;

      // Update to the new category slug
      categorySlug = value.data.slug;

      // If category has changed, reset filters and search
      if (previousCategorySlug !== categorySlug) {
        resetFilters();
      }

      // Reset pagination
      resetPagination();

      // Reset dataLoaded flag
      dataLoaded = false;

      // Load initial games (page 0)
      promise = loadGames(false);
    });
  });

  /**
   * Clean up subscriptions when component is destroyed
   */
  onDestroy(() => {
    if (pageSubscription) {
      pageSubscription();
    }
  });

  /**
   * Reset all filters
   */
  function resetFilters() {
    gameProviderId = '';
    tag = '';
    searchValue = '';

    // Clear search input field
    const searchBar = document.getElementById('searchBar') as HTMLInputElement;
    if (searchBar) searchBar.value = '';
  }

  /**
   * Reset pagination state
   */
  function resetPagination() {
    currentPage = 0;
    nextPage = 1;
    games = [];
  }

  /**
   * Load more games (next page)
   */
  function loadMore() {
    if (loading) return;

    // Use nextPage for loading more games
    currentPage = nextPage;
    loadGames(true);
  }

  /**
   * Load games from API
   * @param appendResults Whether to append results to existing games or replace them
   * @returns Promise with games data
   */
  async function loadGames(appendResults = true): Promise<IGame[]> {
    if (loading && appendResults) return games;

    loading = true;

    const queryParams = {
      size: 32,
      page: currentPage,
      gameProviderId,
      tags: tag,
      name: searchValue,
      gameCategorySlug: categorySlug,
      platformSupport: 'DESKTOP_SUPPORT',
    };

    try {
      const response = await gameService.getAllGames(queryParams);
      const responseData = response.data.data || [];

      if (appendResults) {
        // Create a Set of existing game IDs for quick lookup
        const existingIds = new Set(games.map(g => g.id));

        // Only add games that don't already exist
        const uniqueNewGames = responseData.filter(game => !existingIds.has(game.id));

        games = [...games, ...uniqueNewGames];
        // Update nextPage for the next "Load More" click
        nextPage = response.data.currentPage + 1;
      } else {
        games = responseData;
        // Reset pagination when loading fresh results
        currentPage = 0;
        nextPage = 1;
      }

      totalElements = response.data.totalElements;
      dataLoaded = true; // Mark that data has been loaded
      return games;
    } catch (error) {
      console.error("Error loading games:", error);
      throw error;
    } finally {
      loading = false;
    }
  }

  /**
   * Search games by name
   */
  function searchGames() {
    const searchBar = document.getElementById('searchBar') as HTMLInputElement;
    searchValue = searchBar.value;
    resetPagination();
    dataLoaded = false;
    promise = loadGames(false);
  }

  /**
   * Filter games by provider
   * @param providerId The provider ID to filter by
   */
  function selectedProvider(providerId: string) {
    gameProviderId = providerId;
    resetPagination();
    dataLoaded = false;
    promise = loadGames(false);
  }

  /**
   * Filter games by tag
   * @param tagId The tag ID to filter by
   */
  function selectedTag(tagId: string) {
    tag = tagId;
    resetPagination();
    dataLoaded = false;
    promise = loadGames(false);
  }

  /**
   * Format category slug for display
   * @param slug The category slug to format
   * @returns Formatted heading
   */
  const formatHeading = (slug: string): string => {
    if (!slug) return '';

    const formattedData = slug.replaceAll("-", " ");
    if (!formattedData.length) return '';

    return formattedData.charAt(0).toUpperCase() + formattedData.slice(1);
  };
</script>

<div class="my-8 flex flex-col md:flex-row justify-between items-center gap-2">
  <input
          type="text"
          on:input={searchGames}
          id="searchBar"
          placeholder="Search"
          class="border-2 border-primary-lightBlue outline-0 py-2 px-5 rounded-full w-full bg-transparent text-sm text-white"
  />
  <div class="flex gap-2">
    <ProviderDropDown
            on:selected={(v) => selectedProvider(v.detail.id)}
            id="slotsPageProviderDropdown"
            category={categorySlug}
            css="border-2 border-primary-lightBlue py-2 md:px-5 px-4 text-sm bg-primary-dark rounded-full w-full transition-all flex items-center text-nowrap"
    />
    <TagDropdown
            gameCategorySlug={categorySlug}
            on:selected={(v) => selectedTag(v.detail.slug)}
            id="tagsDropdown"
            css="border-2 border-primary-lightBlue py-2 md:px-5 px-3 text-sm bg-primary-dark w-full rounded-full transition-all flex items-center"
    />
  </div>
</div>

<div class="my-8">
  <h1 class="text-primary-white font-bold text-xl mb-4">{formatHeading(categorySlug)}</h1>
  <div class="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3 md:gap-4">
    {#if loading && !dataLoaded}
      <div class="min-h-[40vh] flex justify-center items-center col-span-full">
        <enhanced:img src="$lib/logo.svg?enhanced" class="animate-bounce" width="140" alt="Forza.bet"/>
      </div>
    {:else}
      {#if games.length > 0}
        {#each games as game (game.id)}
          <GameCard {game}/>
        {/each}
      {:else if dataLoaded}
        <p class="col-span-full text-primary-white min-h-32 items-center flex justify-center">No games found</p>
      {/if}
    {/if}
  </div>

  {#if games.length > 0 && nextPage * 32 <= totalElements}
    <LoadMoreButton
            currentPage={nextPage + 1}
            totalElements={totalElements}
            loadMore={loadMore}
            loading={loading}
    />
  {/if}
</div>